import React, { useState, useEffect } from 'react';
import Slider from 'react-slick';
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { useLanguage } from './LanguageContext';
import OptimizedImage from './components/OptimizedImage';
import uwe from './assets/team/uwe_veronik_uwe.webp';
import mariam from './assets/team/uwe_veronik_mariam.webp';
import emma from './assets/team/uwe_veronik_emma.webp';
import samir from './assets/team/uwe_veronik_samir.webp';
import mariia from './assets/team/uwe_veronik_mariia.webp';
import lily from './assets/team/uwe_veronik_lily.webp';
import samuel from './assets/team/uwe_veronik_samuel.webp';
import louis from './assets/team/uwe_veronik_louis.webp';

const TeamIntro = () => {
  const { t } = useLanguage();

  // Add state to track which card is flipped
  const [flippedIndex, setFlippedIndex] = useState(null);
  // Always use 10px for centerPadding
  const centerPadding = '10px';

  const containerStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    paddingRight: '1rem',
    paddingLeft: '1rem',
    fontFamily: 'Sora, sans-serif',
  };

  const contentStyle = {
    width: '100%',
    maxWidth: '768px',
    textAlign: 'center',
    paddingBlock: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  };

  const headingStyle = {
    color: '#666666',
    fontWeight: '500',
    lineHeight: '1.1',
    letterSpacing: '-1px',
    fontSize: '1.8rem',
    marginBottom: '1rem',
    textAlign: 'center'
  };

  const textStyle = {
    color: '#666666',
    lineHeight: '1.6',
    fontWeight: '300',
    fontSize: '1rem',
    textAlign: 'center',
    maxWidth: '800px',
    margin: '0 auto',
    marginBottom: '2rem'
  };

  const buttonStyle = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    width: '40px',
    height: '40px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'white',
    border: 'none',
    borderRadius: '50%',
    cursor: 'pointer',
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    zIndex: 2,
    transition: 'all 0.3s ease',
  };

  const PrevArrow = ({ onClick, currentSlide }) => (
    <button
      style={{ ...buttonStyle, left: '0', opacity: currentSlide === 0 ? 0.5 : 1 }}
      onClick={onClick}
      disabled={currentSlide === 0}
      aria-label="Previous slide"
    >
      <i className="fas fa-chevron-left" style={{ color: '#666666' }}></i>
    </button>
  );

  const NextArrow = ({ onClick }) => (
    <button
      style={{ ...buttonStyle, right: '0' }}
      onClick={onClick}
      aria-label="Next slide"
    >
      <i className="fas fa-chevron-right" style={{ color: '#666666' }}></i>
    </button>
  );

  const flipCardContainerStyle = {
    perspective: '1000px',
    width: '100%',
    maxWidth: '338px',
    minWidth: '0', // important for flexbox
    height: '415px', // ✅ Needed for images to show
    position: 'relative',
    borderRadius: '5px',
    cursor: 'pointer',
    background: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingInline: '10px',
  };

  const flipCardInnerStyle = (isFlipped) => ({
    position: 'relative',
    width: '100%',
    height: '100%',
    textAlign: 'center',
    transition: 'transform 0.6s cubic-bezier(0.4,0.2,0.2,1)',
    transformStyle: 'preserve-3d',
    borderRadius: '5px',
    transform: isFlipped ? 'rotateY(180deg)' : 'none',
  });

  const flipCardFaceStyle = {
    position: 'absolute',
    width: '100%',
    height: '100%',
    backfaceVisibility: 'hidden',
    borderRadius: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const flipCardBackStyle = {
    ...flipCardFaceStyle,
    background: 'white',
    color: '#666666',
    transform: 'rotateY(180deg)',
    fontFamily: 'Sora, sans-serif',
    fontWeight: 500,
    fontSize: '1.2rem',
    letterSpacing: '-0.5px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '1rem',
    textAlign: 'center',
    justifyContent: 'center',
    alignItems: 'center',
    display: 'flex',
  };

  const imageStyle = {
    width: '100%',
    maxWidth: '338px',
    height: '100%',
    objectFit: 'cover',
    borderRadius: '5px',
  };

  const teamMembers = [
    { image: uwe, name: 'Uwe Veronik' },
    { image: mariam, name: 'Mariam Al-Mehiawi' },
    { image: emma, name: 'Emma Ehrenberg' },
    { image: samuel, name: 'Samuel De Luca' },
    { image: louis, name: 'Louis Reger' },
    { image: mariia, name: 'Mariia Ivashchenko' },
    { image: samir, name: 'Samir Chehade i.A.' },
    { image: lily, name: 'Lily Wambach Rezeption' },
  ];

  const settings = {
    dots: false,
    infinite: true, // <-- No wrap-around, Uwe always on the left
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    prevArrow: <PrevArrow />,
    nextArrow: <NextArrow />,
    centerMode: true,
    centerPadding,
    adaptiveHeight: false,
    centerPadding: '40px',
    centerMode: true,
    responsive: [
      {
        breakpoint: 1024, // iPad/tablet
        settings: {
          initialSlide: 1,
          slidesToShow: 2, // <-- Align left
        }
      },
      {
        breakpoint: 768, // mobile
        settings: {
          slidesToShow: 1,
          initialSlide: 0, // <-- Start with Uwe on mobile
        }
      },
      {
        breakpoint: 3000, // desktop
        settings: {
          slidesToShow: 3,
          initialSlide: 1,
        }
      }
    ]
  };

  return (
    <section id="team" style={containerStyle}>
      <div style={contentStyle}>
        <h1 style={headingStyle}>{t('team')}</h1>
        <p style={textStyle}>{t('teamText')}</p>
        <div
          style={{
            width: '100%',
            maxWidth: `${338 * 3}px`,
            margin: '0 auto',
            overflow: 'hidden'
          }}
        >
          <Slider {...settings}>
            {teamMembers.map((member, index) => (
              <div key={index}>
                <div
                  style={flipCardContainerStyle}
                  tabIndex={0}
                  aria-label={`Flip card for ${member.name}`}
                  onClick={() => setFlippedIndex(flippedIndex === index ? null : index)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      setFlippedIndex(flippedIndex === index ? null : index);
                    }
                  }}
                >
                  <div style={flipCardInnerStyle(flippedIndex === index)}>
                    <div style={flipCardFaceStyle}>
                      <OptimizedImage
                        src={member.image}
                        alt={member.name}
                        style={imageStyle}
                      />
                    </div>
                    <div style={flipCardBackStyle}>
                      {member.name}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </div>
    </section>
  );
};

export default TeamIntro;
